/**
 * NotificationPanel Component - AccountsGenie
 * Dropdown panel for displaying and managing notifications
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Trash2,
  Settings,
  Filter,
  X,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Button } from './button';
import { Badge } from './badge';
import { ScrollArea } from './scroll-area';
import { NotificationItem } from './notification-item';
import { cn } from '../../lib/utils';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { NotificationType, NotificationPriority, Notification } from '../../types/notifications';

interface NotificationPanelProps {
  children?: React.ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  children,
  className,
  align = 'end',
  sideOffset = 4
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<{
    type?: NotificationType;
    priority?: NotificationPriority;
    isRead?: boolean;
  }>({});

  const {
    notifications,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    getUnreadCount
  } = useNotificationsStore();

  // Fetch notifications when panel opens
  useEffect(() => {
    if (isOpen && notifications.length === 0) {
      fetchNotifications();
    }
  }, [isOpen, notifications.length, fetchNotifications]);

  // Filter notifications based on current filter
  const filteredNotifications = notifications.filter(notification => {
    if (filter.type && notification.type !== filter.type) return false;
    if (filter.priority && notification.priority !== filter.priority) return false;
    if (filter.isRead !== undefined && notification.isRead !== filter.isRead) return false;
    return true;
  });

  const unreadCount = getUnreadCount();
  const hasNotifications = notifications.length > 0;
  const hasUnread = unreadCount > 0;

  const handleNotificationAction = (notification: Notification) => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
    }
    
    // Mark as read if it's unread
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleDeleteAllRead = () => {
    deleteAllRead();
  };

  const handleClearFilter = () => {
    setFilter({});
  };

  const handleViewAllNotifications = () => {
    navigate('/notifications'); // Future notifications page
    setIsOpen(false);
  };

  const handleNotificationSettings = () => {
    navigate('/settings?tab=notifications');
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0 relative",
              hasUnread && "text-blue-600",
              className
            )}
            aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`}
          >
            <Bell className="h-4 w-4" />
            {hasUnread && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center min-w-[1.25rem] rounded-full"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-80 max-h-[400px] p-0"
        align={align}
        sideOffset={sideOffset}
        side="bottom"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center space-x-2">
            <Bell className="h-4 w-4" />
            <DropdownMenuLabel className="p-0 font-semibold">
              Notifications
            </DropdownMenuLabel>
            {hasUnread && (
              <Badge variant="secondary" className="text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            {/* Filter indicator */}
            {(filter.type || filter.priority || filter.isRead !== undefined) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilter}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                title="Clear filters"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
            
            {/* Settings */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNotificationSettings}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              title="Notification settings"
            >
              <Settings className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        {hasNotifications && (
          <div className="px-4 py-2 border-b bg-muted/30">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                {hasUnread && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAllAsRead}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Mark all read
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteAllRead}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear read
                </Button>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleViewAllNotifications}
                className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
              >
                View all
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="relative">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading notifications...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8 px-4">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-2">Failed to load notifications</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchNotifications()}
                  className="text-xs"
                >
                  Try again
                </Button>
              </div>
            </div>
          )}

          {!isLoading && !error && !hasNotifications && (
            <div className="flex items-center justify-center py-8 px-4">
              <div className="text-center">
                <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-1">No notifications</p>
                <p className="text-xs text-muted-foreground">You're all caught up!</p>
              </div>
            </div>
          )}

          {!isLoading && !error && filteredNotifications.length === 0 && hasNotifications && (
            <div className="flex items-center justify-center py-8 px-4">
              <div className="text-center">
                <Filter className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-1">No matching notifications</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilter}
                  className="text-xs"
                >
                  Clear filters
                </Button>
              </div>
            </div>
          )}

          {!isLoading && !error && filteredNotifications.length > 0 && (
            <ScrollArea className="max-h-[300px]">
              <div className="divide-y">
                {filteredNotifications.slice(0, 10).map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onMarkAsUnread={markAsUnread}
                    onDelete={deleteNotification}
                    onAction={handleNotificationAction}
                    compact={true}
                    showActions={true}
                    className="border-l-0 bg-transparent hover:bg-muted/50"
                  />
                ))}
              </div>
              
              {filteredNotifications.length > 10 && (
                <div className="p-4 text-center border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleViewAllNotifications}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    View {filteredNotifications.length - 10} more notifications
                  </Button>
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationPanel;